// =============================================================================
// MoneyHostsField — hosty sprzedazowe kompaktowo: top 8 chipow + „i N kolejnych
// chronionych automatycznie" + rozwijana pelna lista z szukaniem i toggle.
// Ochrona domyslnie obejmuje KAZDY host zewnetrzny (G1 blokuje tylko, gdy link
// zmienil sie w tresci — a tresc idzie verbatim, wiec to nic nie kosztuje).
// =============================================================================

import { useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Check, X } from "lucide-react";
import type { DetectedHost } from "@/services/staticSite/wpSiteDetect";

interface Props {
  detected: DetectedHost[];
  value: string[];
  onChange: (hosts: string[]) => void;
}

const TOP = 8;
export const normHost = (h: string) => h.trim().toLowerCase().replace(/^https?:\/\//, "").replace(/\/.*$/, "").replace(/^www\./, "");

export function MoneyHostsField({ detected, value, onChange }: Props) {
  const [showAll, setShowAll] = useState(false);
  const [search, setSearch] = useState("");
  const [manual, setManual] = useState("");

  const rows = useMemo(() => {
    const map = new Map<string, DetectedHost>();
    for (const d of detected) map.set(d.host, d);
    for (const h of value) if (!map.has(h)) map.set(h, { host: h, count: 0, pages: 0 });
    return [...map.values()].sort((a, b) => b.count - a.count || a.host.localeCompare(b.host));
  }, [detected, value]);

  const on = (h: string) => value.includes(h);
  const toggle = (h: string) => onChange(on(h) ? value.filter((x) => x !== h) : [...value, h]);
  const top = rows.filter((r) => on(r.host)).slice(0, TOP);
  const protectedCount = value.length;
  const rest = Math.max(0, protectedCount - top.length);
  const excluded = rows.length - protectedCount;
  const filtered = search ? rows.filter((r) => r.host.includes(search.toLowerCase())) : rows;

  const addManual = () => {
    const h = normHost(manual);
    if (h && !value.includes(h)) onChange([...value, h]);
    setManual("");
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-1.5">
        {top.map((r) => (
          <Badge key={r.host} variant="secondary" className="gap-1 font-mono text-[11px]" title={r.count ? `${r.count} linków · ${r.pages} stron` : "dodany ręcznie"}>
            {r.host}{r.count > 0 && <span className="opacity-60">{r.count}</span>}
          </Badge>
        ))}
        {rest > 0 && <span className="text-xs text-muted-foreground">i {rest} kolejnych hostów chronionych automatycznie</span>}
        {protectedCount === 0 && <span className="text-xs text-muted-foreground">Pusta lista = każdy link zewnętrzny traktowany jako sprzedażowy.</span>}
        {excluded > 0 && <span className="text-xs text-muted-foreground">· {excluded} wyłączonych</span>}
        <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setShowAll((s) => !s)}>{showAll ? "zwiń" : `pokaż wszystkie (${rows.length})`}</Button>
      </div>
      {showAll && (
        <div className="space-y-2 rounded-md border p-2">
          <div className="flex gap-2">
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="szukaj hosta" className="h-8 font-mono text-xs" />
            <Input value={manual} onChange={(e) => setManual(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addManual()} placeholder="dodaj ręcznie, np. klient.pl" className="h-8 font-mono text-xs" />
            <Button size="sm" variant="outline" className="h-8" onClick={addManual} disabled={!normHost(manual)}>Dodaj</Button>
          </div>
          <div className="grid gap-0.5 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.slice(0, 300).map((r) => (
              <button key={r.host} type="button" onClick={() => toggle(r.host)} className={`flex items-center gap-2 rounded px-2 py-1 text-left text-xs transition ${on(r.host) ? "bg-emerald-500/10" : "opacity-60 hover:opacity-100"}`}>
                {on(r.host) ? <Check className="h-3.5 w-3.5 shrink-0 text-emerald-500" /> : <X className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
                <span className="truncate font-mono">{r.host}</span>
                <span className="ml-auto shrink-0 text-muted-foreground">{r.count > 0 ? `${r.count} · ${r.pages} str.` : "ręczny"}</span>
              </button>
            ))}
            {filtered.length > 300 && <span className="px-2 text-xs text-muted-foreground">… zawęź szukaniem ({filtered.length})</span>}
          </div>
        </div>
      )}
    </div>
  );
}
